import { setDailyStats } from "@lib/chart-utils";
import HttpClient from "@lib/http-client";

/**
 * The Trek Mobile URL for a career's artwork, or `null` when nothing names the
 * career's `transfrId`.
 *
 * Three ways in, in order: the id outright, the simulation catalog keyed by
 * `learningExperienceId`, or the career catalog keyed by `careerId`. The
 * simulation catalog carries only careers that have a Trek VR sim, so a career
 * known to Trek Mobile alone resolves through `careers`.
 *
 * @param {object} args
 * @param {string} [args.learningExperienceId]
 * @param {number} [args.careerId]
 * @param {Array<{ learningExperienceId?: string, careerTransfrId?: string }>} [args.simulations]
 * @param {Array<{ id?: number, transfrId?: string }>} [args.careers]
 * @param {string} [args.transfrId]
 * @returns {string | null}
 */
export function resolveCareerImageUrl({
  learningExperienceId,
  careerId,
  simulations,
  careers,
  transfrId,
}) {
  const effectiveTransfrId =
    transfrId ??
    simulations?.find((s) => s.learningExperienceId === learningExperienceId)
      ?.careerTransfrId ??
    (careerId == null
      ? undefined
      : careers?.find((c) => c.id === careerId)?.transfrId);

  if (!effectiveTransfrId) {
    return null;
  }

  return `${process.env.CESE_FE_URL}/images/careers/${effectiveTransfrId}/questions/1.png`;
}

export default class TrekService extends HttpClient {
  constructor(...args) {
    super(...args);
  }

  async getCareerImageUrl({ learningExperienceId, simulations, transfrId }) {
    return resolveCareerImageUrl({
      learningExperienceId,
      simulations,
      transfrId,
    });
  }

  getSkillImage(imageId) {
    return `${process.env.CESE_FE_URL}/images/skills/${imageId}.png`;
  }

  async getFeatureFlags() {
    return await this.http.get("/feature-flags");
  }

  async getCareerPlan(userId) {
    return await this.http.get(
      `/users/${userId}/career-plan?include_lightcast_data=true`
    );
  }

  async getBudgetPlan(userId) {
    return await this.http.get(`/users/${userId}/budget-plan`, {
      params: {
        from_career_plan: true,
      },
    });
  }

  async getUsersExportData({ classroomId, userId, options }) {
    const defaultOptions = {
      include_explored_careers: true,
      include_selected_career: true,
      include_career_goals: true,
      include_favorited_careers: true,
      include_lightcast_data: false,
      include_budget_plans_submitted: true,
    };
    return await this.http.post(`exports/users`, {
      classroom_ids: [classroomId],
      user_id: userId,
      options: { ...defaultOptions, ...options },
    });
  }

  async getInsights({ orgCode, filters, daily, options }) {
    const { classroomIds, dateRange } = filters ?? {};
    const defaultOptions = {
      include_active_users: true,
      include_plans_submitted: true,
      include_careers_explored: false,
      include_budget_plans_submitted: true,
    };

    const casedFilters = {};
    if (dateRange) {
      const { startDate: start_date, endDate: end_date } = dateRange;
      casedFilters.date_range = {
        start_date,
        end_date,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    }

    return await this.http
      .post(
        `exports/users/${daily ? "daily-" : ""}summary`,
        {
          classroom_ids: classroomIds,
          filters: casedFilters,
          options: { ...defaultOptions, ...options },
        },
        {
          headers: {
            organization: orgCode,
          },
        }
      )
      .then((response) =>
        daily ? setDailyStats(filters, response) : response
      );
  }

  async getInsightsGrouped({ orgCode, filters }) {
    const { classroomIds, dateRange } = filters ?? {};

    const casedFilters = {};
    if (dateRange) {
      const { startDate: start_date, endDate: end_date } = dateRange;
      casedFilters.date_range = {
        start_date,
        end_date,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    }

    return await this.http.post(
      `exports/users/grouped-summary`,
      {
        classroom_ids: classroomIds,
        filters: casedFilters,
      },
      {
        headers: {
          organization: orgCode,
        },
      }
    );
  }

  /**
   * Careers ranked by how often each was chosen in a submitted career plan, each
   * with the learners who chose it.
   *
   * `organizationCodes` narrows a multi-organization scope to selected children;
   * omitted, the organization header's whole subtree is read. `userId` narrows to
   * a single learner. `includeArchivedLearners` widens the roster the plans are
   * read for; archiving flags a learner's membership rather than removing them,
   * and the roster hides them by default.
   *
   * @param {object} options
   * @param {string} [options.orgCode] - Organization header the scope is read
   *   through.
   * @param {object} [options.filters]
   * @param {string[]} [options.filters.classroomIds]
   * @param {string[]} [options.filters.organizationCodes]
   * @param {string} [options.filters.userId]
   * @param {boolean} [options.filters.includeArchivedLearners]
   * @param {{ startDate?: string, endDate?: string }} [options.filters.dateRange]
   */
  async getTopCareers({ orgCode, filters }) {
    const {
      classroomIds,
      organizationCodes,
      userId,
      includeArchivedLearners,
      dateRange,
    } = filters ?? {};

    const casedFilters = {};
    if (dateRange) {
      const { startDate: start_date, endDate: end_date } = dateRange;
      casedFilters.date_range = {
        start_date,
        end_date,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
    }
    if (organizationCodes?.length) {
      casedFilters.organization_codes = organizationCodes;
    }

    return await this.http.post(
      "exports/users/top-careers",
      {
        classroom_ids: classroomIds,
        ...(userId ? { user_id: userId } : {}),
        // The roster is resolved before the request body's own filters are read,
        // so this scopes it from the body root the way `classroom_ids` does.
        ...(includeArchivedLearners ? { include_archived_learners: true } : {}),
        filters: casedFilters,
      },
      {
        headers: {
          organization: orgCode,
        },
      }
    );
  }

  async getUserJourneyStatus(userId) {
    return await this.http.get(`users/${userId}/journey-status`);
  }

  async getPathwayPDF(careerPlanId) {
    return await this.http.get(
      `reports/user-pathway?career_plan_id=${careerPlanId}`,
      {
        responseType: "blob",
      }
    );
  }

  async getSimulations() {
    return await this.http.get("/simulations");
  }

  /**
   * The whole in-house career catalog. Sent unparameterized on purpose: the
   * endpoint's `limit` defaults to "none", so one request returns every career
   * rather than a page, and the response carries the `transfrId` that addresses
   * a career's artwork.
   */
  async getCareers() {
    return await this.http.get("/careers");
  }
}
